import { useEffect, useState } from 'react';
import { AudienceCategorySelector } from '@/components/AudienceCategorySelector';
import { GarmentTypeSelector } from '@/components/GarmentTypeSelector';
import { BrandSearch } from '@/components/BrandSearch';
import { SizeSelect } from '@/components/SizeSelect';
import { ResultCard } from '@/components/ResultCard';
import { FitLegend } from '@/components/FitLegend';
import { AvatarPanel } from '@/components/AvatarPanel';
import { useComparisonForm } from '@/hooks/useComparisonForm';
import { api } from '@/services/api';
import type { ComparisonResult } from '@/types/domain';

export function BrandVsBrandPage() {
  const {
    audience,
    setAudience,
    category,
    setCategory,
    garmentType,
    setGarmentType,
    sourceBrandSlug,
    setSourceBrandSlug,
    sourceSize,
    setSourceSize,
    sourceSizes,
    brands,
    brandsLoading,
  } = useComparisonForm();

  const [targetBrandSlug, setTargetBrandSlug] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'error' | 'success'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [result, setResult] = useState<ComparisonResult | null>(null);

  // Page-specific reset — the shared form state resets itself; this page
  // additionally needs to clear its target brand and result.
  useEffect(() => {
    setTargetBrandSlug('');
    setResult(null);
    setStatus('idle');
  }, [audience, category]);

  async function runComparison() {
    if (audience === 'child') return;
    setStatus('loading');
    setErrorMessage('');
    const res = await api.compareBrandToBrand({
      audience,
      category,
      garmentType,
      sourceBrandSlug,
      sourceSize,
      targetBrandSlug,
    });
    if (!res.success) {
      setStatus('error');
      setErrorMessage(res.message);
      return;
    }
    const data = res.data as { result: ComparisonResult };
    setResult(data.result);
    setStatus('success');
  }

  const canCompare = audience !== 'child' && sourceBrandSlug && sourceSize && targetBrandSlug;

  return (
    <div className="max-w-4xl grid gap-8 lg:grid-cols-[1fr_260px]">
      <div className="space-y-6">
        <AudienceCategorySelector
          audience={audience}
          category={category}
          onAudienceChange={setAudience}
          onCategoryChange={setCategory}
        />

        {audience === 'child' ? (
          <div className="rounded-2xl border border-stone-200 bg-white p-4 text-sm text-muted" role="status">
            Youth sizing comparisons aren't available yet — we only publish a recommendation once a
            brand's youth chart has been independently verified.
          </div>
        ) : (
          <>
            <GarmentTypeSelector group={category} value={garmentType} onChange={setGarmentType} />

            <div className="grid gap-4 sm:grid-cols-2">
              <BrandSearch
                id="bvb-source-brand"
                label="Brand you know"
                brands={brands}
                loading={brandsLoading}
                value={sourceBrandSlug}
                onChange={setSourceBrandSlug}
              />
              <SizeSelect
                id="bvb-source-size"
                label="Your size"
                sizes={sourceSizes}
                value={sourceSize}
                onChange={setSourceSize}
                disabled={!sourceBrandSlug}
              />
            </div>

            <BrandSearch
              id="bvb-target-brand"
              label="Brand you want to buy"
              brands={brands}
              loading={brandsLoading}
              value={targetBrandSlug}
              onChange={setTargetBrandSlug}
              excludeSlug={sourceBrandSlug}
            />

            <button
              type="button"
              onClick={runComparison}
              disabled={!canCompare || status === 'loading'}
              className="min-h-[44px] w-full rounded-xl bg-brand px-4 py-3 text-base font-semibold text-ink shadow-card transition-opacity disabled:opacity-40 sm:w-auto sm:px-8"
            >
              {status === 'loading' ? 'Comparing…' : 'Compare these two brands'}
            </button>

            {status === 'error' && (
              <p role="alert" className="rounded-xl bg-danger/10 p-3 text-sm text-danger">
                {errorMessage}
              </p>
            )}

            {status === 'success' && result && (
              <div className="space-y-4">
                <FitLegend />
                <ResultCard result={result} interactive={false} />
              </div>
            )}
          </>
        )}
      </div>

      <aside className="lg:sticky lg:top-6 lg:self-start">
        <AvatarPanel audience={audience} zones={result?.zones} />
      </aside>
    </div>
  );
}
